import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

export type ModalType =
  | 'global-search'
  | 'new-meeting'
  | 'new-template'
  | 'share-meeting'
  | 'export-data'
  | 'settings'
  | 'notifications'
  | null;

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Modals
  activeModal: ModalType;
  modalData: Record<string, any> | null;

  // Global search
  globalSearchOpen: boolean;
  globalSearchQuery: string;

  // Page state
  currentPage: string;
  pageScrollPositions: Record<string, number>;

  // Preferences (persisted)
  compactMode: boolean;
  showKeyboardShortcuts: boolean;
}

interface UIActions {
  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Modals
  openModal: (modal: ModalType, data?: Record<string, any>) => void;
  closeModal: () => void;
  setModalData: (data: Record<string, any>) => void;

  // Global search
  openGlobalSearch: () => void;
  closeGlobalSearch: () => void;
  setGlobalSearchQuery: (query: string) => void;
  toggleGlobalSearch: () => void;

  // Page state
  setCurrentPage: (page: string) => void;
  saveScrollPosition: (page: string, position: number) => void;
  getScrollPosition: (page: string) => number;

  // Preferences
  setCompactMode: (enabled: boolean) => void;
  toggleCompactMode: () => void;
  setShowKeyboardShortcuts: (show: boolean) => void;

  // Reset
  resetUI: () => void;
}

export type UIStore = UIState & UIActions;

const initialState: UIState = {
  sidebarOpen: false,
  sidebarCollapsed: false,
  activeModal: null,
  modalData: null,
  globalSearchOpen: false,
  globalSearchQuery: '',
  currentPage: '/',
  pageScrollPositions: {},
  compactMode: false,
  showKeyboardShortcuts: true,
};

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Sidebar actions
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen }), false, 'toggleSidebar'),
        setSidebarOpen: (open) => set({ sidebarOpen: open }, false, 'setSidebarOpen'),
        toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }), false, 'toggleSidebarCollapsed'),
        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }, false, 'setSidebarCollapsed'),

        // Modal actions
        openModal: (modal, data = null) => {
          set({
            activeModal: modal,
            modalData: data,
            // Close global search when opening another modal
            globalSearchOpen: modal === 'global-search' ? true : false,
          }, false, 'openModal');
        },
        closeModal: () => set({ activeModal: null, modalData: null }, false, 'closeModal'),
        setModalData: (data) => set({ modalData: data }, false, 'setModalData'),

        // Global search actions
        openGlobalSearch: () => set({
          globalSearchOpen: true,
          activeModal: 'global-search'
        }, false, 'openGlobalSearch'),
        closeGlobalSearch: () => set({
          globalSearchOpen: false,
          globalSearchQuery: '',
          activeModal: null
        }, false, 'closeGlobalSearch'),
        setGlobalSearchQuery: (query) => set({ globalSearchQuery: query }, false, 'setGlobalSearchQuery'),
        toggleGlobalSearch: () => {
          const state = get();
          if (state.globalSearchOpen) {
            get().closeGlobalSearch();
          } else {
            get().openGlobalSearch();
          }
        },

        // Page state actions
        setCurrentPage: (page) => set({ currentPage: page }, false, 'setCurrentPage'),
        saveScrollPosition: (page, position) => set((state) => ({
          pageScrollPositions: { ...state.pageScrollPositions, [page]: position }
        }), false, 'saveScrollPosition'),
        getScrollPosition: (page) => get().pageScrollPositions[page] || 0,

        // Preference actions
        setCompactMode: (enabled) => set({ compactMode: enabled }, false, 'setCompactMode'),
        toggleCompactMode: () => set((state) => ({ compactMode: !state.compactMode }), false, 'toggleCompactMode'),
        setShowKeyboardShortcuts: (show) => set({ showKeyboardShortcuts: show }, false, 'setShowKeyboardShortcuts'),

        // Reset
        resetUI: () => set(initialState, false, 'resetUI'),
      }),
      {
        name: 'meetlytic-ui-store',
        // Only persist user preferences
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          compactMode: state.compactMode,
          showKeyboardShortcuts: state.showKeyboardShortcuts,
          pageScrollPositions: state.pageScrollPositions,
        }),
      }
    ),
    { name: 'ui-store' }
  )
);

// Selector hooks for optimized re-renders
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useActiveModal = () => useUIStore((state) => state.activeModal);
export const useGlobalSearchOpen = () => useUIStore((state) => state.globalSearchOpen);
export const useCompactMode = () => useUIStore((state) => state.compactMode);

// Combined selectors - use useShallow to prevent infinite re-renders
export const useSidebarState = () => useUIStore(
  useShallow((state) => ({
    isOpen: state.sidebarOpen,
    isCollapsed: state.sidebarCollapsed,
    toggle: state.toggleSidebar,
    setOpen: state.setSidebarOpen,
  }))
);

export const useGlobalSearchState = () => useUIStore(
  useShallow((state) => ({
    isOpen: state.globalSearchOpen,
    query: state.globalSearchQuery,
    open: state.openGlobalSearch,
    close: state.closeGlobalSearch,
    toggle: state.toggleGlobalSearch,
    setQuery: state.setGlobalSearchQuery,
  }))
);
